import { useEffect, useRef } from "react";

// Varasun ambient motion layer: drifting particle network, traveling data packets
// and floating isometric infrastructure cubes. Density auto-tunes down on small
// screens; pauses offscreen; caps DPR; disables under prefers-reduced-motion.
const AnimatedBackground = ({
    className = "",
    density = 0.00012,
    maxNodes = 120,
    linkDist = 145,
    speed = 0.26,
    tint = "30,144,255",
    cubes = true,
}) => {
    const ref = useRef(null);

    useEffect(() => {
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
        const canvas = ref.current;
        const ctx = canvas.getContext("2d");
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        let raf = null;
        let running = false;
        let w = 0;
        let h = 0;
        let nodes = [];
        let packets = [];
        let cubeDefs = [];
        let spawnTimer = 0;
        let mobile = false;
        let linkDistNow = linkDist;
        let packetCap = 22;

        const resize = () => {
            const rect = canvas.parentElement.getBoundingClientRect();
            w = rect.width;
            h = rect.height;
            mobile = w < 480;
            linkDistNow = mobile ? 95 : linkDist;
            packetCap = mobile ? 7 : 22;
            canvas.width = w * dpr;
            canvas.height = h * dpr;
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            const count = Math.min(maxNodes, Math.max(16, Math.floor(w * h * density * (mobile ? 0.5 : 1))));
            nodes = Array.from({ length: count }, () => ({
                x: Math.random() * w,
                y: Math.random() * h,
                vx: (Math.random() - 0.5) * speed,
                vy: (Math.random() - 0.5) * speed,
                r: Math.random() * 1.6 + 0.7,
            }));
            cubeDefs = [
                { fx: 0.1, fy: 0.28, s: 40, phase: 0, accent: false },
                { fx: 0.88, fy: 0.2, s: 54, phase: 1.4, accent: true },
                { fx: 0.78, fy: 0.78, s: 34, phase: 2.6, accent: false },
                { fx: 0.22, fy: 0.82, s: 44, phase: 3.8, accent: false },
            ];
            if (mobile) cubeDefs = cubeDefs.slice(0, 2).map((c) => ({ ...c, s: c.s * 0.7 }));
            packets = [];
        };

        const strokeCube = (cx, cy, s, accent, t) => {
            const hx = s * 0.866;
            const hy = s * 0.5;
            const v = s * 0.9;
            const stroke = accent ? "rgba(255,176,32,0.2)" : `rgba(${tint},0.18)`;
            const fill = accent ? "rgba(255,176,32,0.06)" : `rgba(${tint},0.06)`;
            ctx.strokeStyle = stroke;
            ctx.lineWidth = 1.1;
            ctx.beginPath();
            ctx.moveTo(cx, cy - hy);
            ctx.lineTo(cx + hx, cy);
            ctx.lineTo(cx, cy + hy);
            ctx.lineTo(cx - hx, cy);
            ctx.closePath();
            ctx.fillStyle = fill;
            ctx.fill();
            ctx.stroke();
            ctx.beginPath();
            ctx.moveTo(cx - hx, cy);
            ctx.lineTo(cx - hx, cy + v);
            ctx.lineTo(cx, cy + hy + v);
            ctx.lineTo(cx, cy + hy);
            ctx.closePath();
            ctx.stroke();
            ctx.beginPath();
            ctx.moveTo(cx + hx, cy);
            ctx.lineTo(cx + hx, cy + v);
            ctx.lineTo(cx, cy + hy + v);
            ctx.stroke();
            const pulse = 0.5 + 0.5 * Math.sin(t * 1.6);
            ctx.beginPath();
            ctx.arc(cx, cy, 1.8 + pulse, 0, Math.PI * 2);
            ctx.fillStyle = accent ? "rgba(255,176,32,0.55)" : "rgba(0,229,255,0.5)";
            ctx.fill();
        };

        const frame = (now) => {
            const t = now / 1000;
            ctx.clearRect(0, 0, w, h);

            if (cubes) {
                for (const c of cubeDefs) {
                    const cx = c.fx * w + Math.sin(t * 0.3 + c.phase) * 12;
                    const cy = c.fy * h + Math.sin(t * 0.45 + c.phase) * 10;
                    strokeCube(cx, cy, c.s, c.accent, t + c.phase);
                }
            }

            for (const n of nodes) {
                n.x += n.vx;
                n.y += n.vy;
                if (n.x < 0 || n.x > w) n.vx *= -1;
                if (n.y < 0 || n.y > h) n.vy *= -1;
                ctx.beginPath();
                ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
                ctx.fillStyle = `rgba(${tint},0.55)`;
                ctx.fill();
            }

            for (let i = 0; i < nodes.length; i++) {
                for (let j = i + 1; j < nodes.length; j++) {
                    const dx = nodes[i].x - nodes[j].x;
                    const dy = nodes[i].y - nodes[j].y;
                    const d = Math.hypot(dx, dy);
                    if (d < linkDistNow) {
                        ctx.beginPath();
                        ctx.moveTo(nodes[i].x, nodes[i].y);
                        ctx.lineTo(nodes[j].x, nodes[j].y);
                        ctx.strokeStyle = `rgba(${tint},${(0.15 * (1 - d / linkDistNow)).toFixed(3)})`;
                        ctx.lineWidth = 0.6;
                        ctx.stroke();
                    }
                }
            }

            spawnTimer -= 1;
            if (spawnTimer <= 0 && packets.length < packetCap && nodes.length > 1) {
                const i = Math.floor(Math.random() * nodes.length);
                let best = -1;
                let bestD = linkDistNow * 1.3;
                for (let j = 0; j < nodes.length; j++) {
                    if (j === i) continue;
                    const d = Math.hypot(nodes[i].x - nodes[j].x, nodes[i].y - nodes[j].y);
                    if (d < bestD) {
                        bestD = d;
                        best = j;
                    }
                }
                if (best >= 0) packets.push({ i, j: best, p: 0, v: 0.009 + Math.random() * 0.013 });
                spawnTimer = mobile ? 40 + Math.random() * 60 : 16 + Math.random() * 28;
            }
            packets = packets.filter((pk) => pk.p <= 1);
            for (const pk of packets) {
                const a = nodes[pk.i];
                const b = nodes[pk.j];
                const x = a.x + (b.x - a.x) * pk.p;
                const y = a.y + (b.y - a.y) * pk.p;
                const fade = Math.sin(pk.p * Math.PI);
                ctx.beginPath();
                ctx.arc(x, y, 1.9, 0, Math.PI * 2);
                ctx.fillStyle = `rgba(0,229,255,${0.9 * fade})`;
                ctx.fill();
                ctx.beginPath();
                ctx.arc(x, y, 5, 0, Math.PI * 2);
                ctx.fillStyle = `rgba(0,229,255,${0.16 * fade})`;
                ctx.fill();
                pk.p += pk.v;
            }

            if (running) raf = requestAnimationFrame(frame);
        };

        resize();
        const io = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting && !running) {
                running = true;
                raf = requestAnimationFrame(frame);
            } else if (!entry.isIntersecting && running) {
                running = false;
                if (raf) cancelAnimationFrame(raf);
            }
        });
        io.observe(canvas);
        window.addEventListener("resize", resize);
        return () => {
            running = false;
            if (raf) cancelAnimationFrame(raf);
            io.disconnect();
            window.removeEventListener("resize", resize);
        };
    }, [density, maxNodes, linkDist, speed, tint, cubes]);

    return <canvas ref={ref} className={`pointer-events-none ${className}`} aria-hidden="true" />;
};

export default AnimatedBackground;
